import Translations from "@translate/components/Member/Subscription/SubscriptionCardH5/CardList/FeatureComparisonTableV2";
import React, { memo } from "react";
import { cn } from "utils";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import {
  type EdCompleteFeatureRow,
  getFeatureComparisonRows,
  getLifetimeExclusiveRows,
} from "@components/Member/shared/edCompleteFeatures";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

interface FeatureComparisonTableV2Props {
  className?: string;
}

const StatusCell = memo(
  ({ included, isComplete }: { included: boolean; isComplete: boolean }) => (
    <div className="flex size-6 items-center justify-center">
      <Icon
        name={included ? "check" : "none"}
        size={included ? 24 : 20}
        color={
          included
            ? isComplete
              ? ospThemes.colors.content_brand_primary
              : ospThemes.colors.content_quaternary
            : ospThemes.colors.content_quaternary
        }
      />
    </div>
  ),
);
StatusCell.displayName = "StatusCell";

const PlanColumns = memo(
  ({
    rows,
    showHeaders = true,
  }: {
    rows: EdCompleteFeatureRow[];
    showHeaders?: boolean;
  }) => (
    <div className="flex shrink-0 gap-3">
      {[
        { label: I18n.t("Free"), isComplete: false },
        { label: I18n.t("Complete"), isComplete: true },
      ].map(({ label, isComplete }) => (
        <div
          key={label}
          className={cn(
            "flex w-16 flex-col items-center px-2",
            showHeaders ? "gap-6 py-4" : "gap-3",
          )}
        >
          {showHeaders && (
            <span
              className={cn(
                "body_medium_m whitespace-nowrap text-center",
                isComplete
                  ? "text-content_brand_primary"
                  : "text-content_tertiary",
              )}
            >
              {label}
            </span>
          )}
          <div className="flex flex-col items-center gap-3">
            {rows.map((item) => (
              <div
                key={`${label}_${item.key}`}
                className="flex h-6 items-center justify-center"
              >
                <StatusCell
                  included={isComplete ? item.completeValue : item.freeValue}
                  isComplete={isComplete}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  ),
);
PlanColumns.displayName = "PlanColumns";

const FeatureComparisonTableV2: React.FC<FeatureComparisonTableV2Props> = memo(
  ({ className }) => {
    const mainRows = getFeatureComparisonRows();
    const lifetimeRows = getLifetimeExclusiveRows();

    return (
      <div className={cn("mx-auto w-full max-w-[1104px] md:px-10", className)}>
        <div
          className="flex w-full flex-col gap-2.5 rounded-3 border border-border_inverse_transparent10 pb-4 pl-4 pr-[15px]"
          data-openreplay-label="member_featurecomparisontablev2_mobile"
        >
          <div className="flex w-full items-start">
            <div className="flex min-w-0 flex-1 flex-col gap-6 py-4 pr-2">
              <span className="button_l text-content_primary">
                {I18n.t("What you get")}
              </span>
              <div className="flex flex-col gap-3">
                {mainRows.map((item) => (
                  <div key={item.key} className="flex h-6 items-center">
                    <span className="body_medium_m text-content_primary">
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <PlanColumns rows={mainRows} />
          </div>

          <div className="flex w-full flex-col gap-3">
            <span className="button_l text-content_brand_primary">
              {I18n.t("Lifetime exclusives")}
            </span>
            <div className="flex w-full items-start">
              <div className="flex min-w-0 flex-1 flex-col gap-3 pr-2">
                {lifetimeRows.map((item) => (
                  <div key={item.key} className="flex h-6 items-center">
                    <span className="body_medium_m text-content_brand_primary">
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
              <PlanColumns rows={lifetimeRows} showHeaders={false} />
            </div>
          </div>
        </div>
      </div>
    );
  },
);

FeatureComparisonTableV2.displayName = "FeatureComparisonTableV2";

export default FeatureComparisonTableV2;
